import React, { useState, useEffect, useCallback } from 'react';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import recruitingService from '../utils/recruitingService';
import './HiresByMonthChart.css';

/**
 * HiresByMonthChart Component
 * Shows monthly hires trend with avg time to hire line
 */
function HiresByMonthChart({ filters }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await recruitingService.getHiresByMonth({
                months: 12,
                ...filters
            });
            if (response.success && response.data) {
                setData(response.data);
            }
        } catch (err) {
            console.error('Error fetching hires by month:', err);
            setData([]);
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-label">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="tooltip-value" style={{ color: entry.color }}>
                            {entry.name}: {entry.value}{entry.name.includes('Days') ? ' days' : ''}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="hires-by-month-chart chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="hires-by-month-chart chart-empty">
                <p>No hires data available</p>
            </div>
        );
    }

    // Calculate totals for header
    const totalHires = data.reduce((sum, d) => sum + d.hires, 0);
    const avgTimeToHire = data.length > 0
        ? Math.round(data.reduce((sum, d) => sum + (d.avgTimeToHire || 0), 0) / data.filter(d => d.avgTimeToHire).length)
        : 0;

    return (
        <div className="hires-by-month-chart">
            <div className="chart-header">
                <div className="chart-title-section">
                    <h3 className="chart-title">Hiring Trend</h3>
                    <span className="chart-subtitle">Monthly hires and time-to-hire</span>
                </div>
                <div className="chart-stats">
                    <div className="stat-item">
                        <span className="stat-value">{totalHires}</span>
                        <span className="stat-label">Total Hires</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{avgTimeToHire}</span>
                        <span className="stat-label">Avg Days</span>
                    </div>
                </div>
            </div>
            <div className="chart-body">
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="monthLabel"
                            tick={{ fontSize: 12, fill: '#7F8C8D' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                        />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 12, fill: '#7F8C8D' }}
                            tickLine={false}
                            axisLine={false}
                            label={{ value: 'Hires', angle: -90, position: 'insideLeft', fontSize: 12, fill: '#7F8C8D' }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12, fill: '#7F8C8D' }}
                            tickLine={false}
                            axisLine={false}
                            label={{ value: 'Days', angle: 90, position: 'insideRight', fontSize: 12, fill: '#7F8C8D' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ fontSize: '12px' }}
                            iconType="circle"
                        />
                        <Bar
                            yAxisId="left"
                            dataKey="hires"
                            name="Hires"
                            fill="#27AE60"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={40}
                        />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="avgTimeToHire"
                            name="Avg Days to Hire"
                            stroke="#E67E22"
                            strokeWidth={2}
                            dot={{ fill: '#E67E22', strokeWidth: 0, r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default HiresByMonthChart;
